export const SUPPORTED_LOCALES = ["en", "es", "pt"] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = "en";
export const FALLBACK_LOCALE: AppLocale = "en";
export const LOCALE_COOKIE = "empiriq-locale";

export function isSupportedLocale(value: unknown): value is AppLocale {
  return SUPPORTED_LOCALES.includes(value as AppLocale);
}

const INTL_LOCALE_BY_APP_LOCALE: Record<AppLocale, string> = {
  en: "en-US",
  es: "es-ES",
  pt: "pt-BR",
};

export function resolveLocale(locale: string | null | undefined): AppLocale {
  return isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;
}

export function toIntlLocale(locale: string | null | undefined): string {
  return INTL_LOCALE_BY_APP_LOCALE[resolveLocale(locale)];
}

export function matchAcceptLanguage(header: string | null): AppLocale | null {
  if (!header) return null;

  const ranges = header
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const qParam = params.map((param) => param.trim()).find((param) => param.startsWith("q="));
      const quality = qParam ? Number(qParam.slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), quality };
    })
    .filter(({ tag, quality }) => tag.length > 0 && Number.isFinite(quality) && quality > 0)
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of ranges) {
    const base = tag.split("-")[0];
    if (isSupportedLocale(base)) return base;
  }

  return null;
}

export function getClientLocale(): AppLocale {
  if (typeof document === "undefined") return DEFAULT_LOCALE;

  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]*)`));
  let cookieLocale: string | undefined;
  if (match) {
    try {
      cookieLocale = decodeURIComponent(match[1]);
    } catch {
      cookieLocale = undefined;
    }
  }
  if (isSupportedLocale(cookieLocale)) return cookieLocale;

  const documentLocale = document.documentElement.lang;
  if (isSupportedLocale(documentLocale)) return documentLocale;

  return DEFAULT_LOCALE;
}
